/**
 * UDC (Universal Data Component) React Hooks
 *
 * Custom hooks for UDC table data management
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { udcService } from '@/api/services/udc.service';
import type {
  UDCRequestParams,
  UDCResponse,
  UDCFilter,
  UDCHeader,
} from '@/api/types/udc.types';

/**
 * Main hook for UDC table data
 *
 * @param collection - Collection name (e.g., 'tickets', 'leads')
 * @param initialParams - Initial request parameters
 * @returns Table data, header, and control functions
 */
export function useUDCTable<T = any>(
  collection: string,
  initialParams: UDCRequestParams = {}
) {
  const [params, setParams] = useState<UDCRequestParams>({
    page: 1,
    per_page: 20,
    include_meta: true,
    ...initialParams,
  });

  const queryClient = useQueryClient();

  // Generate or retrieve selection ID from sessionStorage
  const [selectionId] = useState(() => {
    const storageKey = `${collection}_selection_id`;
    let id = sessionStorage.getItem(storageKey);
    if (!id) {
      // Use crypto.randomUUID if available, otherwise fallback to simple UUID generator
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : generateSimpleUUID();
      sessionStorage.setItem(storageKey, id);
    }
    return id;
  });

  // Fetch table data
  const { data, isLoading, error, refetch } = useQuery<UDCResponse<T>>({
    queryKey: [collection, 'table-data', params],
    queryFn: () =>
      udcService.fetchTableData<T>(collection, {
        ...params,
        selection_id: selectionId,
      }),
    staleTime: 0, // Don't cache data (changes frequently)
  });

  // Update params (triggers refetch)
  const updateParams = useCallback((updates: Partial<UDCRequestParams>) => {
    setParams((prev) => ({ ...prev, ...updates }));
  }, []);

  // Helper methods
  const setPage = useCallback(
    (page: number) => {
      updateParams({ page });
    },
    [updateParams]
  );

  const setPerPage = useCallback(
    (perPage: number) => {
      updateParams({ per_page: perPage, page: 1 });
    },
    [updateParams]
  );

  const setSearch = useCallback(
    (search: string) => {
      updateParams({ search, page: 1 });
    },
    [updateParams]
  );

  const setFilters = useCallback(
    (filters: UDCFilter[], usage?: string) => {
      updateParams({ filters, filter_usage: usage, page: 1 });
    },
    [updateParams]
  );

  const setOrder = useCallback(
    (order: string) => {
      updateParams({ order });
    },
    [updateParams]
  );

  const clearFilters = useCallback(() => {
    updateParams({ filters: [], filter_usage: undefined, page: 1 });
  }, [updateParams]);

  return {
    // Data
    data: data?.data || [],
    header: data?.header,
    summary: data?.header?.summary,
    columns: data?.header?.columns || [],
    availableFilters: data?.header?.filtering?.available_filters || [],
    activeFilters: data?.header?.filtering?.filters || [],
    bulkSelect: data?.header?.bulk_select,

    // State
    isLoading,
    error,
    params,
    selectionId,

    // Actions
    updateParams,
    setPage,
    setPerPage,
    setSearch,
    setFilters,
    setOrder,
    clearFilters,
    refetch,
  };
}

/**
 * Hook for bulk selection management
 *
 * @param collection - Collection name
 * @param selectionId - Selection session UUID
 * @returns Selection management functions
 */
export function useUDCSelection(collection: string, selectionId: string) {
  const queryClient = useQueryClient();

  const selectMutation = useMutation({
    mutationFn: (itemIds: string[]) =>
      udcService.selectItems(collection, selectionId, itemIds),
    onSuccess: () => {
      // Invalidate table data to refresh selection state
      queryClient.invalidateQueries({ queryKey: [collection, 'table-data'] });
    },
  });

  const deselectMutation = useMutation({
    mutationFn: (itemIds: string[]) =>
      udcService.deselectItems(collection, selectionId, itemIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [collection, 'table-data'] });
    },
  });

  const selectAllMutation = useMutation({
    mutationFn: () => udcService.selectAll(collection, selectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [collection, 'table-data'] });
    },
  });

  const clearMutation = useMutation({
    mutationFn: () => udcService.clearSelection(collection, selectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [collection, 'table-data'] });
    },
  });

  const executeBulkActionMutation = useMutation({
    mutationFn: ({
      url,
      method,
      data,
    }: {
      url: string;
      method: string;
      data?: any;
    }) => udcService.executeBulkAction(url, method, data),
    onSuccess: () => {
      // Refresh table data after bulk action
      queryClient.invalidateQueries({ queryKey: [collection, 'table-data'] });
    },
  });

  return {
    // Actions
    selectItems: selectMutation.mutate,
    deselectItems: deselectMutation.mutate,
    selectAll: selectAllMutation.mutate,
    clearSelection: clearMutation.mutate,
    executeBulkAction: executeBulkActionMutation.mutate,

    // Loading states
    isSelecting: selectMutation.isPending,
    isDeselecting: deselectMutation.isPending,
    isExecuting: executeBulkActionMutation.isPending,
  };
}

/**
 * Hook for debounced search
 *
 * @param onSearch - Callback when search value changes
 * @param delay - Debounce delay in ms (default: 300)
 */
export function useDebouncedSearch(
  onSearch: (value: string) => void,
  delay: number = 300
) {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      // Debounce
      const timer = setTimeout(() => {
        setDebouncedValue(value);
        onSearch(value);
      }, delay);

      return () => clearTimeout(timer);
    },
    [onSearch, delay]
  );

  return {
    searchValue,
    debouncedValue,
    setSearchValue: handleSearchChange,
  };
}

/**
 * Simple UUID generator fallback
 * Used when crypto.randomUUID is not available
 */
function generateSimpleUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
